import React, { useState, useEffect, useContext, useRef } from 'react';
import MatchGroupContext from '../../context/matchGroups/matchGroupContext';
import MatchGroupItem from './MatchGroupItem';
import MatchGroupModal from './MatchGroupModal';
import ShareGroupModal from './ShareGroupModal';
import InvitationsModal from './InvitationsModal';
import PageHeader from '../ui/PageHeader';
import '../pages/Dashboard.css';

const MatchGroups = () => {
  const matchGroupContext = useContext(MatchGroupContext);
  const {
    matchGroups,
    getMatchGroups,
    deleteMatchGroup,
    loading
  } = matchGroupContext;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isInvitationsModalOpen, setIsInvitationsModalOpen] = useState(false);
  const [currentGroup, setCurrentGroup] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOption, setSortOption] = useState('newest');
  
  // Add a ref to track if we've already fetched the groups
  const didFetchRef = useRef(false);

  useEffect(() => {
    // Fetch matchgroups when component mounts or when directed to refetch
    getMatchGroups();
    didFetchRef.current = true;
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreateGroup = () => {
    setCurrentGroup(null);
    setIsModalOpen(true);
  };

  const handleEditGroup = (group) => {
    setCurrentGroup(group);
    setIsModalOpen(true);
  };

  const handleDeleteGroup = (groupId) => {
    if (window.confirm('Are you sure you want to delete this group? Matches in this group will not be deleted.')) {
      deleteMatchGroup(groupId);
    }
  };

  const handleShareGroup = (group) => {
    setCurrentGroup(group);
    setIsShareModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentGroup(null);
  };

  const handleCloseShareModal = () => {
    setIsShareModalOpen(false);
    setCurrentGroup(null);
  };

  const handleOpenInvitations = () => {
    setIsInvitationsModalOpen(true);
  };

  const handleCloseInvitations = () => {
    setIsInvitationsModalOpen(false);
  };

  // Filter match groups based on search term
  const filteredGroups = matchGroups.filter(group => {
    return (
      group.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (group.description && group.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  });

  // Sort match groups
  const sortedGroups = [...filteredGroups].sort((a, b) => {
    switch (sortOption) {
      case 'newest':
        return new Date(b.date) - new Date(a.date);
      case 'oldest':
        return new Date(a.date) - new Date(b.date);
      case 'a-z':
        return a.name.localeCompare(b.name);
      case 'z-a':
        return b.name.localeCompare(a.name);
      case 'most-matches':
        const bCount = (b.matches?.tcg?.length || 0) + (b.matches?.vgc?.length || 0);
        const aCount = (a.matches?.tcg?.length || 0) + (a.matches?.vgc?.length || 0);
        return bCount - aCount;
      default:
        return 0;
    }
  });

  if (loading) {
    return (
      <div className="loader">
        <div className="loader-spinner" />
      </div>
    );
  }

  return (
    <div className="page-wide">
      <PageHeader
        title="Match Groups"
        subtitle="Organize your matches by tournament or event"
        actions={
          <>
            <button onClick={handleCreateGroup} className="btn btn-primary">
              <i className="fas fa-plus" /> Create New Group
            </button>
            <button onClick={handleOpenInvitations} className="btn btn-secondary">
              <i className="fas fa-envelope" /> View Invitations
            </button>
          </>
        }
      />

      <div className="match-filters">
        <input
          type="text"
          placeholder="Search groups..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="form-control"
        />
        <select
          value={sortOption}
          onChange={e => setSortOption(e.target.value)}
          className="form-control sort-dropdown"
        >
          <option value="newest">Newest First</option>
          <option value="oldest">Oldest First</option>
          <option value="a-z">Group Name (A-Z)</option>
          <option value="z-a">Group Name (Z-A)</option>
          <option value="most-matches">Most Matches</option>
        </select>
      </div>

      {sortedGroups.length > 0 ? (
        <div className="match-list">
          {sortedGroups.map(group => (
            <MatchGroupItem
              key={group._id}
              group={group}
              onEdit={handleEditGroup}
              onDelete={handleDeleteGroup}
              onShare={handleShareGroup}
            />
          ))}
        </div>
      ) : (
        <div className="no-matches">
          <h3>No match groups found</h3>
          <p>Create a group to organize your matches by tournament or event.</p>
        </div>
      )}

      <MatchGroupModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        initialGroup={currentGroup}
      />

      <ShareGroupModal
        isOpen={isShareModalOpen}
        onClose={handleCloseShareModal}
        group={currentGroup}
      />

      <InvitationsModal
        isOpen={isInvitationsModalOpen}
        onClose={handleCloseInvitations}
      />
    </div>
  );
};

export default MatchGroups;